import { z } from "zod";

import { FilesError } from "./types";
import { PutError, type PutBlob } from "./put";

/**
 * A big upload (WS-27), as the doc service plans it (`crates/services/doc/src/files/upload_routes.rs`): the service
 * says whether the file goes through it as before (`direct`), in one presigned PUT to the storage (`single`), or in
 * parts, each with its own presigned URL (`multipart`). The browser uploads with progress, then tells the service,
 * which checks the bytes and records the version. Stopping it, or a failure, throws what went up away.
 */

export const planSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("direct"), maxBytes: z.number().int().positive() }),
  z.object({
    mode: z.literal("single"),
    uploadId: z.string().min(1),
    url: z.string().min(1),
  }),
  z.object({
    mode: z.literal("multipart"),
    uploadId: z.string().min(1),
    partBytes: z.number().int().positive(),
    parts: z
      .array(z.object({ number: z.number().int().positive(), url: z.string().min(1) }))
      .min(1),
  }),
]);
export type UploadPlan = z.infer<typeof planSchema>;
type Stored = Exclude<UploadPlan, { mode: "direct" }>;

export interface PartTag {
  readonly number: number;
  readonly etag: string;
}

export interface BigUploadSettings {
  readonly partsInFlight: number;
  readonly retries: number;
  readonly retryDelayMs: number;
}

export interface BigUploadDeps {
  readonly put: PutBlob;
  readonly settings: BigUploadSettings;
  /** Waits `ms`; tests make it instant. */
  readonly sleep: (ms: number) => Promise<void>;
}

const HTTP_CLIENT_ERROR = 400;
const HTTP_REQUEST_TIMEOUT = 408;
const HTTP_SERVER_ERROR = 500;
const NO_ETAG =
  "The storage did not give back a part's tag. Its CORS rule must expose the ETag header; ask whoever runs it.";
const STORAGE_FAILED =
  "The file could not be sent to the project's storage. Check your connection and try again.";
const STOPPED = "The upload was stopped.";

/** A failure worth another try: no answer at all, or the storage's own trouble; never a refusal (expired, denied). */
function retriable(error: unknown): boolean {
  if (!(error instanceof PutError) || error.aborted) return false;
  return (
    error.status === 0 || error.status >= HTTP_SERVER_ERROR || error.status === HTTP_REQUEST_TIMEOUT
  );
}

function stopped(signal: AbortSignal | undefined): FilesError | undefined {
  return signal?.aborted === true ? new FilesError("unexpected", STOPPED) : undefined;
}

async function putWithRetries(
  deps: BigUploadDeps,
  url: string,
  body: Blob,
  onProgress: (sent: number) => void,
  signal: AbortSignal | undefined,
): Promise<string | undefined> {
  const { retries, retryDelayMs } = deps.settings;
  for (let attempt = 0; ; attempt += 1) {
    const early = stopped(signal);
    if (early !== undefined) throw early;
    try {
      return (await deps.put({ url, body, onProgress, signal })).etag;
    } catch (error) {
      onProgress(0);
      if (stopped(signal) !== undefined) throw new FilesError("unexpected", STOPPED);
      if (attempt >= retries || !retriable(error)) {
        const refused = error instanceof PutError && error.status >= HTTP_CLIENT_ERROR;
        const detail = refused
          ? "The storage refused the upload; its link may have run out. Try again."
          : STORAGE_FAILED;
        throw new FilesError(refused ? "unavailable" : "network", detail);
      }
      await deps.sleep(retryDelayMs * 2 ** attempt);
    }
  }
}

/** Sends every part, at most `partsInFlight` at a time, and the tags the storage gave them. */
async function sendParts(
  plan: Extract<Stored, { mode: "multipart" }>,
  file: Blob,
  deps: BigUploadDeps,
  report: (sent: number) => void,
  signal: AbortSignal | undefined,
): Promise<PartTag[]> {
  const sent = new Map<number, number>();
  const tags: PartTag[] = [];
  const queue = [...plan.parts];
  let failure: unknown;
  const worker = async (): Promise<void> => {
    for (let part = queue.shift(); part !== undefined; part = queue.shift()) {
      if (failure !== undefined) return;
      const start = (part.number - 1) * plan.partBytes;
      const body = file.slice(start, Math.min(start + plan.partBytes, file.size));
      const { number } = part;
      try {
        const etag = await putWithRetries(
          deps,
          part.url,
          body,
          (bytes) => {
            sent.set(number, bytes);
            report([...sent.values()].reduce((total, each) => total + each, 0));
          },
          signal,
        );
        if (etag === undefined) throw new FilesError("unexpected", NO_ETAG);
        tags.push({ number, etag });
      } catch (error) {
        failure ??= error;
        return;
      }
    }
  };
  const workers = Math.min(deps.settings.partsInFlight, queue.length);
  await Promise.all(Array.from({ length: workers }, worker));
  if (failure !== undefined) throw failure;
  return tags.sort((a, b) => a.number - b.number);
}

/** What the upload needs of the doc service's routes. */
export interface BigUploadCalls<Finished> {
  /** Tells the service the bytes are in the storage; it answers with the recorded file. */
  finish(uploadId: string, parts: readonly PartTag[]): Promise<Finished>;
  /** Throws the upload away, as well as it can. */
  abort(uploadId: string): Promise<void>;
}

/** Uploads `file` as `plan` says, then finishes it; any failure before that aborts the upload. */
export async function sendBig<Finished>(
  plan: Stored,
  file: Blob,
  deps: BigUploadDeps,
  calls: BigUploadCalls<Finished>,
  options: {
    readonly onProgress?: ((sent: number, total: number) => void) | undefined;
    readonly signal?: AbortSignal | undefined;
  },
): Promise<Finished> {
  const report = (sent: number) => options.onProgress?.(sent, file.size);
  report(0);
  let parts: PartTag[] = [];
  try {
    if (plan.mode === "single") await putWithRetries(deps, plan.url, file, report, options.signal);
    else parts = await sendParts(plan, file, deps, report, options.signal);
  } catch (error) {
    await calls.abort(plan.uploadId);
    throw error;
  }
  const early = stopped(options.signal);
  if (early !== undefined) {
    await calls.abort(plan.uploadId);
    throw early;
  }
  report(file.size);
  return calls.finish(plan.uploadId, parts);
}
